import { useCallback } from 'react';
import { DownvoteReason, FeedbackVote, ProjectPaperFeedback } from '../lib/projectPaperFeedback';
import { useProjectPaperFeedback } from '../lib/hooks/useProjectPaperFeedback';

interface UseProjectPaperFeedbackBindingsOptions {
    projectId?: string;
    projectPaperId?: string;
    feedback: ProjectPaperFeedback | null;
    surface: string;
    onOptimisticChange: (nextFeedback: ProjectPaperFeedback | null) => void;
}

function buildOptimisticFeedback(
    vote: FeedbackVote,
    reason: DownvoteReason = null
): ProjectPaperFeedback {
    return {
        vote,
        downvote_reason: vote === 'downvote' ? reason : null,
        updated_at: new Date().toISOString(),
    };
}

export function useProjectPaperFeedbackBindings({
    projectId,
    projectPaperId,
    feedback,
    surface,
    onOptimisticChange,
}: UseProjectPaperFeedbackBindingsOptions) {
    const {
        setFeedback,
        clearFeedback,
        isPending,
        errorMessage,
        clearError,
    } = useProjectPaperFeedback({ projectId, surface });

    const handleSetFeedback = useCallback(async (
        vote: FeedbackVote,
        reason: DownvoteReason = null
    ) => {
        if (!projectId || !projectPaperId) {
            return;
        }

        const previousFeedback = feedback;
        const optimisticFeedback = buildOptimisticFeedback(vote, reason);
        onOptimisticChange(optimisticFeedback);

        try {
            await setFeedback(projectPaperId, vote, reason, previousFeedback);
        } catch {
            onOptimisticChange(previousFeedback);
        }
    }, [feedback, onOptimisticChange, projectId, projectPaperId, setFeedback]);

    const handleClearFeedback = useCallback(async () => {
        if (!projectId || !projectPaperId) {
            return;
        }

        const previousFeedback = feedback;
        onOptimisticChange(null);

        try {
            await clearFeedback(projectPaperId, previousFeedback);
        } catch {
            onOptimisticChange(previousFeedback);
        }
    }, [clearFeedback, feedback, onOptimisticChange, projectId, projectPaperId]);

    return {
        onSetFeedback: handleSetFeedback,
        onClearFeedback: handleClearFeedback,
        feedbackPending: projectPaperId ? isPending(projectPaperId) : false,
        feedbackErrorMessage: errorMessage,
        clearFeedbackError: clearError,
    };
}
